import { useMemo } from 'react'
import moment from 'moment'
import { Text } from '@/ui'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { centsToDollars } from '@/utils/currencyHelpers'
import { selectJournalEntrLineById } from '@/reducers/admin/journalEntryLineReducer'
import { useReselector } from '@/utils/sharedHooks'
import { TextColors, TypographyStyles } from '@/styles'
import { JournalEntryType } from '@/features/journal-entries/types'

const LineItemJournalEntry = ({
  journalEntryId,
}: {
  journalEntryId: number
}) => {
  const journalEntryLine = useReselector(
    selectJournalEntrLineById,
    journalEntryId
  )

  const title = useMemo(
    () => (
      <Text variant={TypographyStyles.BODY_SM} className="mr-4">
        {moment
          .utc(journalEntryLine.journalEntry.journalEntryDate)
          .format('MMM D, YYYY')}
      </Text>
    ),
    [journalEntryLine.journalEntry.journalEntryDate]
  )

  const amount = useMemo(() => {
    const multiplier =
      journalEntryLine.entryType === JournalEntryType.DEBIT ? -1 : 1
    const value = centsToDollars(journalEntryLine.amountInCents) * multiplier
    return (
      <Text variant={TypographyStyles.BODY_SM} className="text-end">
        <CurrencyFormatLabel value={value} />
      </Text>
    )
  }, [journalEntryLine.amountInCents, journalEntryLine.entryType])

  const description = useMemo(
    () => (
      <Text variant={TypographyStyles.BODY_SM} className="w-full">
        &nbsp;{journalEntryLine.journalEntry.transaction?.description || ''}
      </Text>
    ),
    [journalEntryLine.journalEntry.transaction?.description]
  )

  const note = useMemo(
    () => (
      <Text
        variant={TypographyStyles.BODY_SM}
        className="w-full"
        color={TextColors.MUTED}
      >
        {journalEntryLine.journalEntry.details || ''}
      </Text>
    ),
    [journalEntryLine.journalEntry.details]
  )

  if (!journalEntryLine) return null

  return (
    <div className="flex flex-wrap">
      <div className="w-full md:w-3/12">{title}</div>
      <div className="w-full md:w-6/12">{description}</div>
      <div className="w-full md:w-5/12">{note}</div>
      <div className="w-full md:w-2/12 text-right">{amount}</div>
    </div>
  )
}

export { LineItemJournalEntry }
